import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useReaderAuth } from '../context/ReaderAuthContext';
import { 
  BookOpen, ShoppingCart, LogIn, Star, CheckCircle, 
  Shield, Smartphone, Monitor, Loader2 
} from 'lucide-react';

const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/6oUcN4etP3qP4kZfC38so01";

const EbookLanding = () => {
  const navigate = useNavigate();
  const { isAuthenticated, hasEbookAccess, isLoading } = useReaderAuth();
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);

  // Redirect if already has access
  useEffect(() => {
    if (!isLoading && isAuthenticated() && hasEbookAccess()) {
      navigate('/ebook/library');
    }
  }, [isLoading, isAuthenticated, hasEbookAccess, navigate]);

  const handleBuyClick = () => {
    if (isAuthenticated()) {
      // Redirect to Stripe
      window.location.href = STRIPE_PAYMENT_LINK;
    } else {
      setShowAuthPrompt(true);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <Helmet><meta name="robots" content="noindex, nofollow, noarchive" /></Helmet>
      {/* Hero Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Book Cover */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_third-leadership/artifacts/yc1pe6jd_font%20cover%20jpg.jpg"
                  alt="Cultural Fluency Without Compromise"
                  className="relative w-72 lg:w-80 shadow-2xl rounded-lg"
                />
              </div>
            </div>

            {/* Book Info */}
            <div className="text-center lg:text-left">
              <p className="text-[#d4af37] font-medium mb-2 tracking-wider uppercase text-sm">
                Premium eBook Experience
              </p>
              <h1 className="text-3xl lg:text-4xl font-bold text-[#2c3e50] font-display leading-tight mb-4">
                CULTURAL FLUENCY<br />WITHOUT COMPROMISE
              </h1>
              <p className="text-lg text-[#5a6c7d] mb-6">
                Thriving Across Cultures with Presence and Integrity
              </p>
              <p className="text-[#2c3e50] font-medium mb-8">
                By Promise Monday
              </p>

              {/* Price */}
              <div className="mb-8">
                <div className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-xl shadow-sm">
                  <span className="text-3xl font-bold text-[#2c3e50]">£9.99</span>
                  <span className="text-[#5a6c7d] text-sm">one-time purchase</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={handleBuyClick}
                  className="flex items-center justify-center gap-2 bg-[#d4af37] text-[#2c3e50] px-8 py-4 rounded-xl font-semibold hover:bg-[#c9a432] transition-all shadow-lg hover:shadow-xl"
                  data-testid="buy-ebook-btn"
                >
                  <ShoppingCart size={20} />
                  Buy eBook Access – £9.99
                </button>
                
                {!isAuthenticated() && (
                  <Link
                    to="/ebook/login"
                    className="flex items-center justify-center gap-2 bg-white text-[#2c3e50] px-8 py-4 rounded-xl font-semibold hover:bg-slate-50 transition-all border border-slate-200"
                    data-testid="signin-btn"
                  >
                    <LogIn size={20} />
                    Sign In
                  </Link>
                )}
                
                {isAuthenticated() && hasEbookAccess() && (
                  <Link
                    to="/ebook/library"
                    className="flex items-center justify-center gap-2 bg-[#1e3a5f] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#1e3a5f]/90 transition-all"
                  >
                    <BookOpen size={20} />
                    Go to Library
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-center text-[#2c3e50] mb-12">
            Premium Reading Experience
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<BookOpen className="w-8 h-8" />}
              title="Kindle-Style Reader"
              description="Clean, distraction-free reading with customizable fonts, themes, and layouts."
            />
            <FeatureCard
              icon={<Star className="w-8 h-8" />}
              title="Highlights & Notes"
              description="Save your favorite passages and add personal notes as you read."
            />
            <FeatureCard
              icon={<Smartphone className="w-8 h-8" />}
              title="Read Anywhere"
              description="Access on web, iOS, and Android. Your progress syncs across all devices."
            />
          </div>
        </div>
      </section>

      {/* Security Badge */}
      <section className="py-12 bg-[#faf8f5]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-3 bg-white px-6 py-3 rounded-full shadow-sm">
            <Shield className="w-5 h-5 text-[#d4af37]" />
            <span className="text-[#5a6c7d] text-sm">
              Secure payment via Stripe • Instant access after purchase
            </span>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-center text-[#2c3e50] mb-8">
            What You'll Learn
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Master the unwritten rules of cross-cultural communication",
              "Discover your unique leadership identity",
              "Navigate bias while staying true to yourself",
              "Build confidence to adapt without apology",
              "Develop cultural situational awareness",
              "Find your authentic voice in any environment",
              "Create a third culture of understanding",
              "Lead with presence and integrity"
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3 p-3">
                <CheckCircle className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <span className="text-[#2c3e50]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-[#d4af37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <LogIn className="w-8 h-8 text-[#d4af37]" />
            </div>
            <h3 className="text-xl font-bold text-[#2c3e50] mb-2">
              Sign In Required
            </h3>
            <p className="text-[#5a6c7d] mb-6">
              Please sign in or create an account before purchasing. This ensures you can access your eBook anytime.
            </p>
            
            <div className="flex flex-col gap-3">
              <Link
                to="/ebook/login?redirect=buy"
                className="w-full py-3 bg-[#d4af37] text-[#2c3e50] rounded-xl font-semibold hover:bg-[#c9a432] transition-all"
              >
                Sign In
              </Link>
              <Link
                to="/ebook/register?redirect=buy"
                className="w-full py-3 bg-[#1e3a5f] text-white rounded-xl font-semibold hover:bg-[#1e3a5f]/90 transition-all"
              >
                Create Account
              </Link>
              <button
                onClick={() => setShowAuthPrompt(false)}
                className="text-[#5a6c7d] hover:text-[#2c3e50] mt-2"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="text-center p-6">
    <div className="w-16 h-16 bg-[#d4af37]/10 rounded-2xl flex items-center justify-center mx-auto mb-4 text-[#d4af37]">
      {icon}
    </div>
    <h3 className="font-semibold text-[#2c3e50] mb-2">{title}</h3>
    <p className="text-[#5a6c7d] text-sm">{description}</p>
  </div>
);

export default EbookLanding;
